import { getRedirectUrl } from '@folio/utils/redirect-utils';
import { useQuery } from '@tanstack/react-query';
import {
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions,
} from 'korint-sdk';
import { useKorintModuleConfig } from 'korint-sdk/react';
import { Loader } from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';
import { WaitingCard } from '../../components/error-views/waiting-card';
import { Center } from '@folio/components/ui/flex';

type WaitingForReviewModuleProps = {
  title: string;
  message: string;
};

/**
 * A static `/waiting` card never re-checks status, so a broker validated while waiting
 * stays stranded. This re-evaluates the redirect on load and navigates away once they
 * are no longer under review.
 */
export function WaitingForReviewModule({ title, message }: WaitingForReviewModuleProps) {
  const location = useLocation();
  const businessRolesConfig = useKorintModuleConfig('businessRolesConfig');

  const { data: user, status: userStatus } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
    refetchOnMount: 'always',
  });

  const { data: { data: distributionRequests } = {}, status: distributionRequestsStatus } =
    useQuery({
      ...viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions(
        user?.brokerageFirmId ?? '',
      ),
      enabled: !!user?.brokerageFirmId,
      refetchOnMount: 'always',
    });

  const isLoading =
    userStatus === 'pending' ||
    (!!user?.brokerageFirmId && distributionRequestsStatus === 'pending');

  if (isLoading) {
    return (
      <Center className="p-8">
        <Loader className="size-8 animate-spin" />
      </Center>
    );
  }

  // On query error, render the card instead of redirecting: an errored requests fetch
  // looks like "no requests" and would misroute a still-pending broker off /waiting.
  const hasAuthoritativeData =
    userStatus === 'success' &&
    (!user?.brokerageFirmId || distributionRequestsStatus === 'success');

  const roleConfig = user?.brokerRole ? businessRolesConfig?.[user.brokerRole] : undefined;
  if (hasAuthoritativeData && roleConfig) {
    const redirectTo = getRedirectUrl(roleConfig.redirectRules, {
      userProducts: user?.productIds ?? [],
      distributionRequests: distributionRequests ?? [],
      brokerageFirmId: user?.brokerageFirmId ?? '',
      policyId: '',
      customerId: '',
    });

    if (redirectTo !== location.pathname) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  return <WaitingCard title={title} message={message} />;
}
